import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Database, Lock, UserCheck, Mail, Cookie, FileText } from 'lucide-react';

export const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const currentDate = new Date().toLocaleDateString('es-ES', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen p-4 bg-gray-50" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card className="shadow-lg border-t-4 border-t-[#8FBC8F]">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Shield className="w-16 h-16 text-[#8FBC8F]" />
            </div>
            <CardTitle className="text-2xl md:text-3xl text-gray-800">
              {t('privacyPolicy')}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-2">
              {t('privacyLastUpdated')}: {currentDate}
            </p>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed">
              {t('privacyIntro')}
            </p>
          </CardContent>
        </Card>

        {/* Data Collected */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-xl">
              <Database className="w-6 h-6 text-blue-600" />
              {t('privacyDataCollected')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">{t('privacyDataCollectedDesc')}</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#8FBC8F] rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyDataItem1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#8FBC8F] rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyDataItem2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#8FBC8F] rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyDataItem3')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#8FBC8F] rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyDataItem4')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#8FBC8F] rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyDataItem5')}</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* How We Use Data */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-xl">
              <FileText className="w-6 h-6 text-green-600" />
              {t('privacyHowWeUse')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">{t('privacyHowWeUseDesc')}</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyUseItem1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyUseItem2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyUseItem3')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyUseItem4')}</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* No Data Sharing */}
        <Card className="shadow-md bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-xl text-green-800">
              <Lock className="w-6 h-6 text-green-600" />
              {t('privacyNoShare')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-green-700 leading-relaxed">
              {t('privacyNoShareDesc')}
            </p>
          </CardContent>
        </Card>

        {/* User Rights */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-xl">
              <UserCheck className="w-6 h-6 text-purple-600" />
              {t('privacyRights')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">{t('privacyRightsDesc')}</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyRightItem1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyRightItem2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyRightItem3')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{t('privacyRightItem4')}</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Cookies */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-xl">
              <Cookie className="w-6 h-6 text-orange-600" />
              {t('privacyCookies')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed">
              {t('privacyCookiesDesc')}
            </p>
          </CardContent>
        </Card>

        {/* Changes */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-xl">
              <FileText className="w-6 h-6 text-gray-600" />
              {t('privacyChanges')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed">
              {t('privacyChangesDesc')}
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="shadow-md border-b-4 border-b-[#8FBC8F]">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-xl">
              <Mail className="w-6 h-6 text-[#8FBC8F]" />
              {t('privacyContact')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">{t('privacyContactDesc')}</p>
            <a 
              href="mailto:allergenapp.mail@gmail.com"
              className="inline-flex items-center gap-2 text-[#8FBC8F] hover:underline font-medium"
            >
              <Mail className="w-4 h-4" />
              allergenapp.mail@gmail.com
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
